// ═══════════════════════════════════════════════════════════════
// V·IAnet Governance — AdminTiposUnidad (migrado al shell · prompt 0471)
// CRUD catálogo de tipos de unidad organizativa
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import {
  fetchTiposUnidadOrg,
  crearTipoUnidadOrganizativa,
  actualizarTipoUnidadOrganizativa,
  eliminarTipoUnidadOrganizativa,
  type TipoUnidadOrganizativa,
} from '../../../../shared/services/api';
import { useI18n } from '../../../../../i18n';
import { C } from '../../../../../lib/design';
import {
  AdminPageHeader,
  AdminTableShell,
  AdminActionButton,
  type AdminTableColumn,
} from '../../../../shared/components/admin';

interface TipoForm {
  nombre: string;
  nombreCorto: string;
  orden: string;
  color: string;
}

const emptyForm: TipoForm = { nombre: '', nombreCorto: '', orden: '0', color: '#2F5496' };

// ─── Iconos SVG inline ───────────────────────────────────────

function EditIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 6h18" />
      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  );
}

function PowerIcon({ activo }: { activo: boolean }) {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke={activo ? '#16A34A' : '#9CA3AF'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 2v10" />
      <path d="M18.4 6.6a9 9 0 1 1-12.77.04" />
    </svg>
  );
}

export function AdminTiposUnidad() {
  const { t } = useI18n();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const adm: any = (t.structure as Record<string, unknown>)?.admin ?? {};
  const [tipos, setTipos] = useState<TipoUnidadOrganizativa[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [modal, setModal] = useState<TipoUnidadOrganizativa | 'new' | null>(null);
  const [toast, setToast] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    fetchTiposUnidadOrg(mostrarInactivos)
      .then((r) => setTipos(r.data ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [mostrarInactivos]);

  useEffect(() => {
    load();
  }, [load]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const handleDelete = async (tipo: TipoUnidadOrganizativa) => {
    const deptos = tipo._count?.departamentos ?? 0;
    if (deptos > 0) {
      showToast(
        adm.cantDeleteTipoConDeptos ?? 'No se puede eliminar: tiene departamentos asociados',
      );
      return;
    }
    const msg = adm.confirmDeleteTipo ?? 'Eliminar este tipo de unidad?';
    if (!window.confirm(msg)) return;
    try {
      await eliminarTipoUnidadOrganizativa(tipo.id);
      load();
      showToast('Eliminado');
    } catch {
      showToast('Error al eliminar');
    }
  };

  const handleToggleActivo = async (tipo: TipoUnidadOrganizativa) => {
    try {
      await actualizarTipoUnidadOrganizativa(tipo.id, { activo: !tipo.activo });
      load();
      showToast(adm.saved ?? 'Guardado');
    } catch {
      showToast('Error');
    }
  };

  const q = search.toLowerCase().trim();
  const filtered = q
    ? tipos.filter(
        (t) =>
          (t.codigo ?? '').toLowerCase().includes(q) ||
          t.nombre.toLowerCase().includes(q) ||
          (t.nombreCorto ?? '').toLowerCase().includes(q),
      )
    : tipos;

  const columns: AdminTableColumn<TipoUnidadOrganizativa>[] = [
    {
      key: 'codigo',
      label: adm.colCodigo ?? 'Codigo',
      sortable: true,
      sortKey: (t) => t.codigo ?? '',
      render: (t) => (
        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: C.azul2 }}>
          {t.codigo && t.codigo.length > 0 ? t.codigo : '—'}
        </span>
      ),
    },
    {
      key: 'nombre',
      label: adm.nombre ?? adm.colNombre ?? 'Nombre',
      sortable: true,
      sortKey: (t) => t.nombre,
      render: (t) => <span style={{ fontWeight: 600 }}>{t.nombre}</span>,
    },
    {
      key: 'nombreCorto',
      label: adm.nombreCorto ?? 'Nombre corto',
      mono: true,
      sortable: true,
      sortKey: (t) => t.nombreCorto ?? '',
      render: (t) => <span>{t.nombreCorto ?? '—'}</span>,
    },
    {
      key: 'orden',
      label: adm.orden ?? 'Orden',
      align: 'center',
      sortable: true,
      sortKey: (t) => t.orden,
      render: (t) => <span>{t.orden}</span>,
    },
    {
      key: 'color',
      label: adm.color ?? 'Color',
      render: (t) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: t.color ?? '#ccc',
              border: '1px solid #D1D5DB',
              display: 'inline-block',
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize: 11,
              color: '#6B7280',
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            {t.color ?? '—'}
          </span>
        </div>
      ),
    },
    {
      key: 'departamentos',
      label: adm.departamentos ?? 'Departamentos',
      align: 'center',
      sortable: true,
      sortKey: (t) => t._count?.departamentos ?? 0,
      render: (t) => <span>{t._count?.departamentos ?? 0}</span>,
    },
    {
      key: 'activo',
      label: adm.activo ?? 'Activo',
      align: 'center',
      width: 90,
      render: (t) => (
        <span
          style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: 10,
            fontSize: 11,
            fontWeight: 600,
            background: t.activo ? '#ECFDF5' : '#FEF2F2',
            color: t.activo ? '#16A34A' : '#DC2626',
          }}
        >
          {t.activo ? 'Activo' : 'Inactivo'}
        </span>
      ),
    },
  ];

  const tooltips = (adm.tooltips as Record<string, string> | undefined) ?? {};

  return (
    <div>
      <AdminPageHeader
        title={adm.tiposUnidad ?? 'Tipos de unidad organizativa'}
        infoTooltip={{
          title: adm.tiposUnidad ?? 'Tipos de unidad organizativa',
          text: 'Catálogo configurable de tipos de unidad organizativa por tenant.\n\nSe usan para clasificar los departamentos: Dirección, Área, Unidad, Equipo, etc.\n\nNo se puede eliminar un tipo si tiene departamentos asociados. Se ordenan por el campo Orden (ascendente) en organigramas y selectores.',
        }}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: adm.search ?? 'Buscar...',
        }}
        onNew={{
          label: adm.newTipoUnidad ?? 'Nuevo tipo',
          onClick: () => setModal('new'),
        }}
        extras={
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              color: '#5A6171',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={mostrarInactivos}
              onChange={(e) => setMostrarInactivos(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            {adm.mostrarInactivos ?? 'Mostrar inactivos'}
          </label>
        }
      />

      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 200,
            background: '#0E243E',
            color: '#fff',
            padding: '10px 20px',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          {toast}
        </div>
      )}

      <AdminTableShell
        columns={columns}
        rows={filtered}
        rowKey={(t) => t.id}
        onRowClick={(t) => setModal(t)}
        loading={loading}
        loadingMessage={((t.common as Record<string, unknown>)?.cargando as string) ?? 'Cargando…'}
        emptyMessage={adm.noResults ?? 'Sin resultados'}
        actions={(tipo) => {
          const deptos = tipo._count?.departamentos ?? 0;
          return (
            <>
              <AdminActionButton
                icon={<EditIcon />}
                label={tooltips.editar ?? 'Editar'}
                onClick={() => setModal(tipo)}
              />
              <AdminActionButton
                icon={<PowerIcon activo={tipo.activo} />}
                label={
                  tipo.activo
                    ? (tooltips.toggleDesactivar ?? 'Desactivar')
                    : (tooltips.toggleActivar ?? 'Activar')
                }
                onClick={() => handleToggleActivo(tipo)}
              />
              <AdminActionButton
                icon={<TrashIcon />}
                label={
                  deptos > 0
                    ? (adm.cantDeleteTipoConDeptos ?? 'Tiene departamentos')
                    : (tooltips.eliminar ?? 'Eliminar')
                }
                variant="danger"
                onClick={() => handleDelete(tipo)}
                disabled={deptos > 0}
              />
            </>
          );
        }}
        footer={
          !loading ? (
            <span>
              {filtered.length} de {tipos.length} {adm.tuoContador ?? 'tipos de unidad'}
            </span>
          ) : undefined
        }
      />

      {modal !== null && (
        <TipoUnidadModal
          tipo={modal === 'new' ? null : modal}
          maxOrden={tipos.length > 0 ? Math.max(...tipos.map((t) => t.orden)) : 0}
          adm={adm}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            load();
            showToast('Guardado');
          }}
        />
      )}
    </div>
  );
}

function TipoUnidadModal({
  tipo,
  maxOrden,
  adm,
  onClose,
  onSaved,
}: {
  tipo: TipoUnidadOrganizativa | null;
  maxOrden: number;
  adm: Record<string, string | undefined>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = tipo !== null;
  const [form, setForm] = useState<TipoForm>(() =>
    isEdit
      ? {
          nombre: tipo.nombre,
          nombreCorto: tipo.nombreCorto ?? '',
          orden: String(tipo.orden),
          color: tipo.color ?? '#2F5496',
        }
      : { ...emptyForm, orden: String(maxOrden + 1) },
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!form.nombre.trim()) {
      setError(adm.required ?? 'Campo obligatorio');
      return;
    }
    try {
      setSaving(true);
      const body = {
        nombre: form.nombre.trim(),
        nombreCorto: form.nombreCorto.trim() || undefined,
        orden: parseInt(form.orden) || 0,
        color: form.color || undefined,
      };
      if (isEdit) await actualizarTipoUnidadOrganizativa(tipo.id, body);
      else await crearTipoUnidadOrganizativa(body);
      onSaved();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setSaving(false);
    }
  };

  const set = (k: keyof TipoForm, v: string) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setError('');
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 12,
          borderTop: '3px solid #1F3864',
          maxWidth: 500,
          width: '95%',
          padding: '24px 28px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}
      >
        <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: '#0E243E' }}>
          {isEdit
            ? (adm.editTipoUnidad ?? 'Editar tipo de unidad')
            : (adm.newTipoUnidad ?? 'Nuevo tipo de unidad')}
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={lblStyle}>Nombre *</label>
            <input
              style={inpStyle}
              value={form.nombre}
              onChange={(e) => set('nombre', e.target.value)}
              autoFocus
            />
          </div>
          <div>
            <label style={lblStyle}>{adm.nombreCorto ?? 'Nombre corto'}</label>
            <input
              style={inpStyle}
              value={form.nombreCorto}
              maxLength={20}
              onChange={(e) => set('nombreCorto', e.target.value)}
            />
          </div>
          <div>
            <label style={lblStyle}>{adm.orden ?? 'Orden'}</label>
            <input
              type="number"
              style={inpStyle}
              value={form.orden}
              onChange={(e) => set('orden', e.target.value)}
            />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={lblStyle}>{adm.color ?? 'Color'}</label>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <input
                type="color"
                value={form.color}
                onChange={(e) => set('color', e.target.value)}
                style={{
                  width: 36,
                  height: 30,
                  border: '1px solid #D1D5DB',
                  borderRadius: 4,
                  cursor: 'pointer',
                  padding: 2,
                }}
              />
              <span
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: form.color,
                  border: '1px solid #D1D5DB',
                  display: 'inline-block',
                }}
              />
              <span
                style={{
                  fontSize: 12,
                  color: '#6B7280',
                  fontFamily: "'JetBrains Mono', monospace",
                }}
              >
                {form.color}
              </span>
            </div>
          </div>
        </div>
        {error && (
          <div
            style={{
              padding: '8px 12px',
              borderRadius: 6,
              marginTop: 12,
              background: '#FEF2F2',
              color: '#DC2626',
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 20px',
              background: 'transparent',
              border: '1px solid #D1D5DB',
              borderRadius: 6,
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'inherit',
              color: '#374151',
            }}
          >
            {adm.cancel ?? 'Cancelar'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '8px 24px',
              background: '#2F5496',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 700,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
              fontFamily: 'inherit',
            }}
          >
            {saving ? 'Guardando...' : (adm.save ?? 'Guardar')}
          </button>
        </div>
      </div>
    </div>
  );
}

const lblStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: '#5A6171',
  marginBottom: 4,
  display: 'block',
};

const inpStyle: React.CSSProperties = {
  padding: '8px 10px',
  border: '1px solid #D1D5DB',
  borderRadius: 6,
  fontSize: 13,
  width: '100%',
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
};
